// src/components/modules/CompletionCertificate.jsx
import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast } from '../ui/toast';
import {
  Award,
  Download,
  Share2,
  X,
  CheckCircle,
  Sparkles,
  Calendar,
  BookOpen,
  ArrowRight
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import useDialogA11y from '../../hooks/useDialogA11y';

// Whose name goes on the certificate: a signed-in user's own name, otherwise
// one they type once. Guests are never blocked from earning it — the name is
// simply editable, kept on this device, and reused next time.
const GUEST_NAME_KEY = 'profoundPhilosophy_certificateName';

// The iOS web view ignores download links, and sharing window.location.href from
// inside the app shares a capacitor://localhost address nobody else can open. So
// in the app the certificate image is written to the app's cache and handed to
// the iOS share sheet, which offers Save Image, Messages, Mail and the rest.
// Pemberton's FileDeliveryService is the same pattern.
const IS_NATIVE = Capacitor.isNativePlatform();

const CompletionCertificate = ({ show, onClose, module, userName }) => {
  const certificateRef = useRef(null);
  const { user } = useAuth();
  const accountName = userName || user?.displayName || null;
  const [guestName, setGuestName] = useState(() => localStorage.getItem(GUEST_NAME_KEY) || '');
  const [editingName, setEditingName] = useState(false);
  const displayedName = accountName || guestName || 'Your name here';
  const dialogRef = useDialogA11y(show, onClose);

  useEffect(() => {
    if (guestName) localStorage.setItem(GUEST_NAME_KEY, guestName);
  }, [guestName]);
  const completionDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const fileName = `${module.title.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '')}-certificate.png`;

  // html2canvas can't paint gradient-clipped text: it draws the gradient as a solid bar.
  // It would also bake in the name field's placeholder. So the copy it renders from gets
  // solid headings and a blank name line; the dialog on screen is untouched.
  const renderImage = async () => {
    const canvas = await html2canvas(certificateRef.current, {
      backgroundColor: '#0f172a',
      scale: 2,
      onclone: (doc) => {
        doc.querySelectorAll('[data-capture-colour]').forEach((el) => {
          const colour = el.getAttribute('data-capture-colour');
          el.style.backgroundImage = 'none';
          el.style.color = colour;
          el.style.webkitTextFillColor = colour;
        });
        doc.querySelectorAll('input[placeholder]').forEach((el) => { el.placeholder = ''; });
      },
    });
    return canvas.toDataURL('image/png');
  };

  const shareImageNatively = async (dataUrl) => {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const written = await Filesystem.writeFile({
      path: fileName,
      data: dataUrl.replace(/^data:image\/png;base64,/, ''),
      directory: Directory.Cache,
    });
    try {
      await Share.share({
        title: 'Certificate of Completion',
        text: `I completed "${module.title}" in Profound Philosophy.`,
        files: [written.uri],
        dialogTitle: 'Save or share your certificate',
      });
    } catch (error) {
      // Dismissing the share sheet rejects; that isn't a failure to report.
      if (!String(error?.message || '').toLowerCase().includes('cancel')) throw error;
    }
  };

  const handleDownload = async () => {
    if (!certificateRef.current) return;

    try {
      const dataUrl = await renderImage();
      if (IS_NATIVE) {
        await shareImageNatively(dataUrl);
        return;
      }
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating certificate:', error);
      showToast('The certificate couldn’t be created just now. Please try again.');
    }
  };

  const handleShare = async () => {
    const text = `I just completed "${module.title}" in Profound Philosophy. 🎉`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Module Completed',
          text: text,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(text);
      showToast('Achievement copied to clipboard', { tone: 'success' });
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md overflow-y-auto"
        onClick={onClose}
      >
        <div className="min-h-full flex items-center justify-center p-4 py-24 relative overflow-hidden">
        {/* Confetti particles */}
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: '50vw',
              y: '50vh',
              opacity: 1,
              scale: 1
            }}
            animate={{
              x: `${Math.random() * 100}vw`,
              y: `${Math.random() * 100}vh`,
              opacity: 0,
              scale: 0,
              rotate: Math.random() * 720
            }}
            transition={{
              duration: 2,
              ease: 'easeOut',
              delay: i * 0.02
            }}
            className="absolute w-2 h-2 rounded-full pointer-events-none"
            style={{
              backgroundColor: ['#FFD700', '#14b8a6', '#a855f7', '#f59e0b', '#ec4899'][i % 5]
            }}
          />
        ))}

        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Module completion certificate"
          initial={{ scale: 0.8, y: 50, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.8, y: 50, opacity: 0 }}
          transition={{ type: 'spring', damping: 15 }}
          className="relative max-w-4xl w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            aria-label="Close certificate"
            className="absolute -top-4 -right-4 z-10 w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center text-white transition-colors shadow-lg border-2 border-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Certificate Card */}
          <div
            ref={certificateRef}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl overflow-hidden border-4 border-double border-amber-500/50"
          >
            {/* Decorative Header */}
            <div className="relative h-32 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 overflow-hidden">
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px'
                }} />
              </div>

              {/* Sparkle decorations */}
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 2, repeat: Infinity }
                }}
                className="absolute top-4 left-8"
              >
                <Sparkles className="w-6 h-6 text-yellow-300" />
              </motion.div>
              <motion.div
                animate={{
                  rotate: -360,
                  scale: [1, 1.3, 1]
                }}
                transition={{
                  rotate: { duration: 15, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 3, repeat: Infinity }
                }}
                className="absolute top-6 right-12"
              >
                <Sparkles className="w-5 h-5 text-amber-200" />
              </motion.div>

              {/* Center award icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
                >
                  <Award className="w-12 h-12 text-purple-600" />
                </motion.div>
              </div>
            </div>

            {/* Certificate Content */}
            <div className="px-6 sm:px-12 py-10 text-center">
              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent"
                data-capture-colour="#fbbf24"
              >
                Certificate of Completion
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-slate-400 text-sm mb-8 uppercase tracking-widest"
              >
                Profound Philosophy
              </motion.p>

              {/* Recipient */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8"
              >
                <p className="text-slate-400 text-sm mb-2">This is to certify that</p>
                {accountName || (guestName && !editingName) ? (
                  <button
                    type="button"
                    onClick={() => { if (!accountName) setEditingName(true); }}
                    title={accountName ? undefined : 'Click to change the name'}
                    aria-label={accountName ? undefined : 'Change the name on the certificate'}
                    className={`text-5xl font-bold text-white mb-1 block mx-auto ${accountName ? 'cursor-default' : 'hover:text-amber-200 transition-colors'}`}
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    {displayedName}
                  </button>
                ) : (
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onBlur={() => setEditingName(false)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditingName(false); } }}
                    placeholder="Type your name"
                    aria-label="Name for your certificate"
                    autoFocus={editingName}
                    className="text-4xl sm:text-5xl font-bold text-white mb-1 bg-transparent border-b-2 border-amber-500/50 focus:border-amber-400 text-center w-full max-w-lg mx-auto placeholder-slate-400"
                    style={{ fontFamily: 'Georgia, serif' }}
                  />
                )}
                <div className="w-64 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-3" />
                {!accountName && (
                  <p className="text-xs text-slate-400 mt-2 no-print" data-html2canvas-ignore="true">
                    Sign in and your account name is used automatically.
                  </p>
                )}
              </motion.div>

              {/* Module Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8"
              >
                <p className="text-slate-400 text-sm mb-3">has successfully completed</p>
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text mb-2" data-capture-colour="#c084fc">
                    {module.title}
                  </h3>
                  {module.subtitle && (
                    <p className="text-lg text-slate-300 italic">
                      {module.subtitle}
                    </p>
                  )}
                </div>
              </motion.div>

              {/* Achievement Details */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap items-center justify-center gap-3 sm:gap-8 mb-8 text-sm"
              >
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-300 whitespace-nowrap">{completionDate}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                  <span className="text-slate-300 whitespace-nowrap">{module.duration || '45 mins'}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-slate-300 whitespace-nowrap">{module.difficulty || 'Foundation'}</span>
                </div>
              </motion.div>

              {/* Decorative Quote */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="max-w-xl mx-auto mb-6"
              >
                <div className="relative">
                  <div className="absolute -left-4 -top-2 text-6xl text-amber-500/20 font-serif">"</div>
                  <p className="text-slate-400 italic text-sm leading-relaxed px-6">
                    The unexamined life is not worth living.
                  </p>
                  <div className="absolute -right-4 -bottom-2 text-6xl text-amber-500/20 font-serif">"</div>
                </div>
                {/* Was a body-language line left over from the app this one was cloned from. */}
                <p className="mt-3 text-xs text-slate-400">Socrates, in Plato’s Apology, 38a</p>
              </motion.div>

              {/* Seal/Badge */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.9 }}
                className="inline-flex items-center justify-center"
              >
                <div className="relative">
                  <div className="w-24 h-24 bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-2xl border-4 border-amber-300/50">
                    <div className="text-center">
                      <Award className="w-10 h-10 text-white mx-auto mb-1" />
                      <p className="text-[8px] text-white font-bold uppercase tracking-wider">Completed</p>
                    </div>
                  </div>
                  {/* Rays emanating from seal */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 -z-10"
                  >
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute top-1/2 left-1/2 w-0.5 h-8 bg-gradient-to-t from-amber-500/50 to-transparent"
                        style={{
                          transform: `rotate(${i * 30}deg) translateY(-50%)`,
                          transformOrigin: 'top center'
                        }}
                      />
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Footer Border */}
            <div className="h-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
          </div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6"
          >
            <div className="flex items-center gap-4">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border-2 border-slate-700 hover:border-slate-600 shadow-lg transition-all hover:scale-105"
              >
                {IS_NATIVE ? <Share2 className="w-5 h-5" /> : <Download className="w-5 h-5" />}
                {IS_NATIVE ? 'Save or share' : 'Download'}
              </button>
              {!IS_NATIVE && (
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border-2 border-slate-700 hover:border-slate-600 shadow-lg transition-all hover:scale-105"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompletionCertificate;
